/**
 * The local-mode auth bypass, defined once (plan §6.8.11 · AD-11).
 *
 * `VITE_DATA_SOURCE=local` + `VITE_AUTH_MODE=disabled` is the hermetic
 * development and unit-test posture: no session, no cookie, no auth routes.
 * `config.ts` refuses the combination in production, so nothing here can ship
 * enabled — this module states the condition, it does not decide it.
 *
 * It is a module of its own rather than an export of `passkey-login.tsx`
 * because four unrelated call sites ask the question — the route guard, the
 * session context in two places, and the login panel — and a component module
 * owning it meant the composition root imported a predicate upward from a leaf
 * it composes while three call sites still spelled the comparison out inline.
 * Four copies of one condition is four things to keep in step; the danger is
 * not the duplication but a future edit landing on some of them.
 *
 * `local-bypass` names the concern, which is what keeps it out of the banned
 * `utils`/`helpers`/`common` bucket (naming §7): extraction moves DOWN the
 * layering into a named leaf, never sideways into a grab bag.
 */
import type { AppConfig } from '@/config';

/** Shown wherever the bypass is active; `LoginPanel` is its sole renderer. */
export const LOCAL_AUTH_BYPASS_COPY = 'Auth disabled in local mode.';

export function isLocalAuthDisabled(config: AppConfig): boolean {
  return config.dataSource === 'local' && config.authMode === 'disabled';
}
